import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SettingsFooter} from '../../common/settingsWrapper';
import {EditorErrorList} from '../../editors/commons/editorErrorList';

/*
 * Props.
 */

interface ResourceEditorFooterProps<E extends string> {
  isEditing: boolean;
  isReadOnly?: boolean;

  formErrors?: ReadonlyArray<E>;
  errorLabels?: Record<E, ReactNode>;

  onCancel: Handler;
  onSave: Handler;

  createLabel: ReactNode;
}

/*
 * Component.
 */

export const ResourceEditorFooter = <E extends string>(props: ResourceEditorFooterProps<E>) => {
  const {isEditing, formErrors = [], errorLabels, onCancel, onSave, createLabel, isReadOnly} = props;

  return (
    <SettingsFooter>
      <Button variant={ButtonVariantsEnum.SECONDARY} isRounded onClick={onCancel}>
        <Trans id="editor-footer-cancel">Cancel</Trans>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={onSave}
        isDisabled={formErrors.length > 0 || isReadOnly}
        shouldDisallowCursorIfDisabled
        renderTooltip={maybeRenderFormErrorsTooltip(formErrors, errorLabels)}
      >
        {isEditing ? <Trans id="editor-footer-save">Save changes</Trans> : createLabel}
      </Button>
    </SettingsFooter>
  );
};

/*
 * Helpers.
 */

function maybeRenderFormErrorsTooltip<E extends string>(
  formErrors: ReadonlyArray<E>,
  errorLabels?: Record<E, ReactNode>,
) {
  if (!errorLabels || formErrors.length === 0) {
    return undefined;
  }

  const errors = formErrors.map((formError) => ({
    key: formError,
    label: errorLabels[formError],
  }));

  return function () {
    return <EditorErrorList errors={errors} />;
  };
}
